import { FC, useEffect, useState } from 'react';

import { useI18nContext } from '../../hooks/contexts/i18n.context';
import * as Styles from './styles';

// Delay between the live region mounting and receiving its message (see comment below).
export const PLACEHOLDER_ANNOUNCE_DELAY_MS = 100;

/**
 * ResaleSoldOutState skeleton while the tickets page is still checking resale
 * ("pending" scenario in `pages/tickets.tsx`). Takes the same area as the final state so the
 * screen isn't blank and doesn't change height when the right state shows up.
 *
 * role="status" is a live region: screen readers announce CHANGES to its text, not its
 * aria-label. So the region mounts empty and the (visually hidden) message is inserted a
 * moment later, once assistive technology has registered the region.
 * No aria-busy: when "true", screen readers may hold back the region's announcement.
 */
const ResaleSoldOutPlaceholder: FC = () => {
  const { translate } = useI18nContext();
  const [announce, setAnnounce] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnnounce(true), PLACEHOLDER_ANNOUNCE_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Styles.Container
      role="status"
      id="resale-sold-out-placeholder"
      data-testid="resale-sold-out-placeholder"
    >
      <Styles.VisuallyHidden id="resale-sold-out-placeholder-message">
        {announce ? translate(['RESALE_LISTING', 'SOLD_OUT_STATE', 'CHECKING']) : ''}
      </Styles.VisuallyHidden>
      <Styles.SkeletonIcon aria-hidden="true" />
      <Styles.Content aria-hidden="true">
        <Styles.Texts $variant="listing">
          <Styles.SkeletonLine $height={44} $mobileHeight={38} $width="420px" />
          <Styles.SkeletonLine $height={24} $width="560px" />
        </Styles.Texts>
        <Styles.SkeletonLine $height={44} $width="240px" />
      </Styles.Content>
    </Styles.Container>
  );
};

export default ResaleSoldOutPlaceholder;
